// Red/Amber/Green indicator for a self-hosted model endpoint, so a user
// understands *warming up* vs *ready* vs *off* (scaled to zero). Polls
// agentApi.modelServingStatus; polls faster while AMBER (warming) so the cold
// start visibly resolves, then settles. Backend mapping: app/services/model_serving_status.py.
import { useEffect, useRef, useState } from "react";
import { agentApi } from "../../api/client";
import { COLORS } from "../../theme/colors";

const _DOT = { green: COLORS.green, amber: COLORS.orange, red: COLORS.pink };

// pure — the dot color for a light; unknown/missing → the neutral dim token.
export function lightColor(light) {
  return _DOT[light] || COLORS.textDim;
}

// Pure presentational view of a serving status ({light,label,detail,steps}).
// Shared by ModelServingLight (fetches by endpoint) and AgentServingLight
// (fetches by agent) so the dot + label + warm-up sequence render identically.
export function ServingLightView({ status }) {
  const warming = status.light === "amber" && Array.isArray(status.steps);
  const _stepColor = (s) => (s === "active" ? _DOT.amber : s === "done" ? _DOT.green : COLORS.textDim);
  const _stepMark = (s) => (s === "done" ? "✓ " : s === "active" ? "⋯ " : "○ ");
  return (
    <span data-testid="serving-light" title={status.detail || ""}
      style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 12.5, color: COLORS.text }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
        <span data-testid="serving-dot" data-light={status.light}
          style={{
            width: 9, height: 9, borderRadius: "50%", background: lightColor(status.light),
            boxShadow: status.light === "amber" ? `0 0 0 3px ${_DOT.amber}22` : "none",
          }} />
        <b>{status.label}</b>
      </span>
      {warming && (
        // the ordered warm-up sequence — the user sees which step is happening now
        <span data-testid="serving-steps" style={{ display: "inline-flex", gap: 10, fontSize: 11 }}>
          {status.steps.map((s) => (
            <span key={s.key} data-testid="serving-step" data-state={s.state}
              style={{ color: _stepColor(s.state), fontWeight: s.state === "active" ? 700 : 500 }}>
              {_stepMark(s.state)}{s.label}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

export default function ModelServingLight({ endpointId, pollMs = 6000, warmingPollMs = 4000 }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await agentApi.modelServingStatus(endpointId);
        if (!alive) return;
        const st = res?.data || res;
        setStatus(st);
        setError(null);
        // reschedule: tighter cadence while warming so the cold start resolves visibly
        clearInterval(timer.current);
        timer.current = setInterval(load, st?.light === "amber" ? warmingPollMs : pollMs);
      } catch (e) {
        if (alive) setError(e?.message || String(e));
      }
    };
    load();
    return () => { alive = false; clearInterval(timer.current); };
  }, [endpointId, pollMs, warmingPollMs]);

  if (error) {
    return <span data-testid="serving-light-error" style={{ color: _DOT.red, fontSize: 12 }}>status unavailable</span>;
  }
  if (!status) {
    return <span data-testid="serving-light-loading" style={{ color: COLORS.textDim, fontSize: 12 }}>…</span>;
  }
  return <ServingLightView status={status} />;
}
